import React, {
  useMemo,
  useId,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import styled from 'styled-components';
import {
  Stack,
  Typography,
  Tooltip as MuiTooltip,
  CircularProgress,
  ToggleButtonGroup,
} from '@mui/material';
import dayjs from 'dayjs';
import Humanize from 'humanize-plus';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  TooltipProps,
  ReferenceLine,
} from 'recharts';
import ToggleButton from '../../components/ToggleButton';
import themeBase from '../../style/theme';
import { Metrics, AnomalyDirection, Period, RunStatus } from '../../types';
import { pluralizeString } from '../../utils/common';
import {
  formatShortDateTime,
  localizeDate,
  timeConsts,
} from '../../utils/datetime';
import Alert from '../Alert';
import DropDown from '../DropDown';
import { Flex, FlexCenter } from '../Flex';
import OverflowTooltip from '../OverflowTooltip';
import AnomalyChartTooltip from './AnomalyChartTooltip';
import ChartSelectionOverlay from './ChartControls/ChartSelectionOverlay';
import ChartZoomControls from './ChartControls/ChartZoomControls';
import useChartSelection, { ZoomRange } from './hooks/useChartSelection';
import { failureCondition, isInDetectionPeriod } from './utils';
import ExcludeConfirmationDialog from './ExcludeConfirmationDialog';

const DOTS_OVERLOAD_LIMIT = 200;

type ChartMetric = Omit<Metrics, 'value'> & {
  value?: number | null;
  fail_on_zero?: boolean;
  anomaly_direction?: AnomalyDirection;
  EXPECTED_AREA?: (number | null | undefined)[];
  DELAY_AREA?: (number | null | undefined)[];
  DETECTION_AREA?: (number | null | undefined)[];
  endTimeTimestamp: number;
  dotsCount?: number;
  isInDetectionPeriod?: boolean;
  index: number;
};

const LoadingWrapper = styled(Flex)`
  z-index: 1;
  position: absolute;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const NoTextSelectWrapper = styled(Stack)<{ $disableTextSelect?: boolean }>`
  border-radius: 8px;
  background-color: ${({ theme }) => theme.palette.common.gray3};
  position: relative;

  .recharts-cartesian-grid,
  .recharts-area {
    cursor: crosshair;
  }

  ${({ $disableTextSelect }) =>
    $disableTextSelect &&
    `
    * {
      user-select: none !important;
    }
  `}
`;

const ChartDot = styled.div<{
  isAbnormal: boolean;
  rangeExists?: boolean;
  disabled?: boolean;
}>`
  width: 8px;
  height: 8px;
  background: ${({ theme, isAbnormal, rangeExists, disabled }) =>
    disabled
      ? themeBase.palette.common.gray18
      : rangeExists
      ? isAbnormal
        ? theme.palette.common.red
        : theme.palette.common.green
      : theme.palette.common.black};
  border-radius: 100%;

  &:hover {
    border: 3px solid
      ${({ isAbnormal, disabled }) =>
        disabled
          ? 'rgba(182, 186, 190, 0.2)'
          : isAbnormal
          ? 'rgba(255, 101, 91, 0.2)'
          : 'rgba(66, 203, 154, 0.2)'};
    box-sizing: content-box;
    background-clip: padding-box;
    -webkit-background-clip: padding-box;
  }
`;

const AreaTitleWrapper = styled(Flex)<{ $active?: boolean }>`
  height: 100%;
  border-radius: 2px 2px 0 0;
  background: ${({ theme, $active }) =>
    $active ? theme.palette.common.gray17 : theme.palette.common.gray5};
  justify-content: center;
  align-items: center;
  overflow: hidden;
  gap: 9px;
  padding: 0 8px;
`;

const AreaTitleLabel = styled.span<{
  $active?: boolean;
}>`
  color: ${({ theme, $active }) =>
    $active ? theme.palette.common.black : theme.palette.common.gray2};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  line-height: normal;
  text-align: center;
  white-space: nowrap;
`;

const AreaTitleEllipsis = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LinearGradient = ({ data, id }: { data: ChartMetric[]; id: string }) => {
  if (!data.length) return null;

  const minTimestamp = data[0].endTimeTimestamp;
  const maxTimestamp = data[data.length - 1].endTimeTimestamp;
  const timestampRange = maxTimestamp - minTimestamp;
  const step = 100 / timestampRange;

  const getStopColor = (point: ChartMetric) =>
    point.isInDetectionPeriod
      ? failureCondition(point.value, point)
        ? themeBase.palette.common.red
        : themeBase.palette.common.green
      : themeBase.palette.common.gray18;

  const getOffset = (timestamp: number) =>
    `${step * (timestamp - minTimestamp)}%`;

  return (
    <linearGradient id={`${id}colorUv`} x1="0%" y1="0" x2="100%" y2="0">
      {data.map((point, idx) => (
        <React.Fragment key={`${point.endTimeTimestamp}-${idx}`}>
          <stop
            offset={getOffset(point.endTimeTimestamp)}
            stopColor={getStopColor(point)}
          />
          {idx > 0 && (
            <stop
              offset={getOffset(data[idx - 1].endTimeTimestamp)}
              stopColor={getStopColor(data[idx - 1])}
            />
          )}
        </React.Fragment>
      ))}
    </linearGradient>
  );
};

const RenderTooltip = (
  props: TooltipProps<any, any> & {
    metricsTitle: string;
    hidden: boolean;
    status: RunStatus;
  },
) => {
  if (props?.hidden) return null;

  const inDetectionPeriod = props?.payload?.[0]?.payload?.isInDetectionPeriod;

  return (
    <AnomalyChartTooltip
      active={!!props.active}
      data={props?.payload?.[0]?.payload}
      metricsTitle={props.metricsTitle}
      rangeColor={
        inDetectionPeriod ? undefined : themeBase.palette.common.gray18
      }
      valueColor={
        inDetectionPeriod ? undefined : themeBase.palette.common.gray18
      }
      status={props.status}
    />
  );
};

const Dot = ({ cx, cy, value, payload, active, onDotHovered }: any) => {
  const isValid = value != null;

  const rangeExists = useMemo(
    () => payload?.min_value != null || payload?.max_value != null,
    [payload],
  );

  const isAbnormal = useMemo(
    () => isValid && failureCondition(value, payload),
    [isValid, payload, value],
  );

  if ((payload?.dotsCount || 0) >= DOTS_OVERLOAD_LIMIT && !active) {
    // Skips points in case there are too much
    return null;
  }

  if (!isValid) return null;

  return (
    <foreignObject
      x={cx - 7}
      y={cy - 7}
      width="14px"
      height="14px"
      onMouseEnter={() => onDotHovered(true)}
      onMouseLeave={() => onDotHovered(false)}
    >
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <ChartDot
          isAbnormal={isAbnormal}
          rangeExists={rangeExists}
          disabled={!payload?.isInDetectionPeriod}
        />
      </Stack>
    </foreignObject>
  );
};

const renderAreaTitles = (
  zoomData: ChartMetric[],
  fullData: ChartMetric[],
  latestRunTime?: string,
  detectionPeriod?: Period,
  detectionDelay?: Period,
) => {
  if (!latestRunTime || !detectionPeriod) return null;

  const detectedData = zoomData.filter((item) =>
    isInDetectionPeriod(detectionPeriod, item.end_time, latestRunTime),
  );

  const totalDetectedData = fullData.filter((item) =>
    isInDetectionPeriod(detectionPeriod, item.end_time, latestRunTime),
  );

  const zoomDataMinutes =
    dayjs(zoomData[zoomData.length - 1].end_time).diff(
      dayjs(zoomData[0].end_time),
      'minute',
    ) || 1;

  const totalDataMinutes =
    dayjs(fullData[fullData.length - 1].end_time).diff(
      dayjs(fullData[0].end_time),
      'minute',
    ) || 1;

  const detectedDataMinutes =
    dayjs(detectedData[detectedData.length - 1]?.end_time).diff(
      dayjs(detectedData[0]?.end_time),
      'minute',
    ) || 1;

  const totalDetectedDataMinutes =
    dayjs(totalDetectedData[totalDetectedData.length - 1]?.end_time).diff(
      dayjs(totalDetectedData[0]?.end_time),
      'minute',
    ) || 1;

  const delayDataMinutes = detectionDelay
    ? detectionDelay.count * timeConsts[detectionDelay.period]
    : 0;

  const detectedPrec = (detectedDataMinutes / zoomDataMinutes) * 100;
  const delayPrec = (delayDataMinutes / zoomDataMinutes) * 100;
  const trainingPrec = 100 - detectedPrec - delayPrec;
  const showDelaySection = detectionDelay && detectionDelay.count > 0;

  const getPeriod = (minutes: number) => {
    const days = Math.ceil(minutes / timeConsts.day);
    const hours = Math.ceil(minutes / timeConsts.hour);

    if (hours < 24) {
      return {
        count: hours,
        period: 'hour',
      } as Period;
    } else {
      return {
        count: days,
        period: 'day',
      } as Period;
    }
  };

  const trainingMinutes =
    zoomDataMinutes - detectedDataMinutes - delayDataMinutes;
  const totalTrainingMinutes =
    totalDataMinutes - totalDetectedDataMinutes - delayDataMinutes;

  const trainingPeriod = getPeriod(trainingMinutes);
  const totalTrainingPeriod = getPeriod(totalTrainingMinutes);
  const detectedPeriod = getPeriod(detectedDataMinutes);
  const delayPeriod = getPeriod(delayDataMinutes);

  const normalizeToMinutes = (period: Period) =>
    period.period === 'hour'
      ? period.count * timeConsts.hour
      : period.count * timeConsts.day;

  const getDisplayedTitle = (period: Period, totalPeriod: Period) => {
    const periodMinutes = normalizeToMinutes(period);
    const totalPeriodMinutes = normalizeToMinutes(totalPeriod);

    const totalPeriodDisplay = `${pluralizeString(
      totalPeriod.count,
      totalPeriod.period,
      `${totalPeriod.period}s`,
    )}`;

    if (periodMinutes === totalPeriodMinutes) {
      return totalPeriodDisplay;
    } else if (period.period === totalPeriod.period) {
      return `${period.count} out of ${totalPeriodDisplay}`;
    } else {
      return `${pluralizeString(
        period.count,
        period.period,
        `${period.period}s`,
      )} out of ${totalPeriodDisplay}`;
    }
  };

  const rightMargin = 78;

  return (
    <foreignObject x={74} y={11} height={16} width="100%">
      <Flex sx={{ width: `calc(100% - ${rightMargin}px)`, height: '100%' }}>
        <MuiTooltip
          placement="bottom"
          title={`The training period is set to ${pluralizeString(
            totalTrainingPeriod.count,
            totalTrainingPeriod.period,
            `${totalTrainingPeriod.period}s`,
          )}`}
        >
          <AreaTitleWrapper sx={{ width: `${trainingPrec}%` }}>
            <AreaTitleEllipsis>
              <AreaTitleLabel>Training Period:</AreaTitleLabel>{' '}
              <AreaTitleLabel $active>
                {getDisplayedTitle(trainingPeriod, totalTrainingPeriod)}
              </AreaTitleLabel>
            </AreaTitleEllipsis>
          </AreaTitleWrapper>
        </MuiTooltip>

        <MuiTooltip
          placement="bottom"
          title={`The detection period is set to ${pluralizeString(
            detectionPeriod.count,
            detectionPeriod.period,
            `${detectionPeriod.period}s`,
          )}`}
        >
          <AreaTitleWrapper sx={{ width: `${detectedPrec}%` }} $active>
            <AreaTitleEllipsis>
              <AreaTitleLabel>Detection Period:</AreaTitleLabel>{' '}
              <AreaTitleLabel $active>
                {getDisplayedTitle(detectedPeriod, detectionPeriod)}
              </AreaTitleLabel>
            </AreaTitleEllipsis>
          </AreaTitleWrapper>
        </MuiTooltip>

        {showDelaySection && (
          <MuiTooltip
            placement="bottom"
            title={`Detection is delayed by ${pluralizeString(
              delayPeriod.count,
              delayPeriod.period,
              `${delayPeriod.period}s`,
            )}`}
          >
            <AreaTitleWrapper sx={{ width: `${delayPrec}%` }}>
              <AreaTitleEllipsis>
                <AreaTitleLabel>Detection Delay:</AreaTitleLabel>{' '}
                <AreaTitleLabel $active>{`${
                  delayPeriod.count
                } ${pluralizeString(
                  delayPeriod.count,
                  delayPeriod.period,
                  `${delayPeriod.period}s`,
                )}`}</AreaTitleLabel>
              </AreaTitleEllipsis>
            </AreaTitleWrapper>
          </MuiTooltip>
        )}
      </Flex>
    </foreignObject>
  );
};

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  .MuiToggleButton-root {
    min-width: 64px;
    height: 24px;
    padding: 0 12px;
    background: ${({ theme }) => theme.palette.common.gray5};
    color: ${({ theme }) => theme.palette.common.black};
    border: 1px solid ${({ theme }) => theme.palette.common.gray};
    font-size: 11px;
    text-transform: uppercase;
    line-height: 1;

    &:first-of-type {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }

    &:last-of-type {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
      margin-left: -1px;
    }

    &:hover {
      background: ${({ theme }) => theme.palette.common.gray14};
    }

    &.Mui-selected {
      background: ${({ theme }) => theme.palette.common.gray14};
      color: ${({ theme }) => theme.palette.common.black};
    }

    &:disabled {
      background: ${({ theme }) => theme.palette.common.gray8};
      border: 1px solid ${({ theme }) => theme.palette.common.gray};
    }
  }

  .MuiButtonBase-root {
    min-height: 0;
    height: 24px;
    max-height: 24px;
  }
`;

const SelectionTimeIndicator = styled.div`
  position: absolute;
  right: 18px;
  bottom: 50px;
  background: rgba(0, 0, 0, 0.5);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.white};
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
  font-weight: 500;
`;

const DimensionValueDropdown = styled(DropDown)`
  max-width: 270px;
  min-width: 150px;
  font-size: 14px;
  font-weight: 500;
`;

const ChartHeaderWrapper = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: 8px 8px 0 8px;
  width: 100%;
`;

const Circle = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
  width: 12px;
  height: 12px;
  border-radius: 100%;
`;

const HeaderLeftSection = styled(Flex)`
  flex: 1 1 auto;
  width: fit-content;
  overflow: hidden;
  align-items: center;
`;

type Props = {
  metrics?: Metrics[];
  status: RunStatus;
  metricsTitle: string;
  failOnZero?: boolean;
  anomalyDirection?: AnomalyDirection;
  detectionPeriod?: Period;
  detectionDelay?: Period;
  loading?: boolean;
  onExcludeRange?: (start: number, end: number) => void;
};

const AnomalyChart = ({
  metrics,
  status,
  metricsTitle,
  failOnZero = false,
  loading = false,
  anomalyDirection = AnomalyDirection.Both,
  detectionPeriod,
  detectionDelay,
  onExcludeRange,
}: Props) => {
  const id = useId();
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [initialZoomRange, setInitialZoomRange] = useState<ZoomRange | null>(
    null,
  );
  const [dotHovered, setDotHovered] = useState(false);
  const [mode, setMode] = useState<'zoom' | 'exclude'>('zoom');

  const [dimensionValue, setDimensionValue] = useState<string | undefined>(
    undefined,
  );

  const dimensionValueOptions = useMemo(() => {
    const dimensionValueAnomalousMap = metrics?.reduce((acc, metric) => {
      if (metric.dimension_value != null) {
        // If this dimension value already exists, mark it as anomalous if ANY occurrence is anomalous
        if (acc[metric.dimension_value] != null) {
          acc[metric.dimension_value] =
            acc[metric.dimension_value] || (metric.is_anomalous ?? false);
        } else {
          acc[metric.dimension_value] = metric.is_anomalous ?? false;
        }
      }
      return acc;
    }, {} as { [key: string]: boolean });

    if (dimensionValueAnomalousMap) {
      const dimensionValueOptions = Object.keys(dimensionValueAnomalousMap)
        .map((dimensionValue) => {
          const isAnomalous = dimensionValueAnomalousMap[dimensionValue];
          return {
            label: dimensionValue,
            value: dimensionValue,
            icon: (
              <Circle
                $color={
                  isAnomalous
                    ? themeBase.palette.common.red
                    : themeBase.palette.common.green
                }
              />
            ),
            isAnomalous: isAnomalous,
          };
        })
        .sort((a, b) =>
          a.isAnomalous && !b.isAnomalous
            ? -1
            : a.isAnomalous === b.isAnomalous
            ? a.label.localeCompare(b.label)
            : 1,
        );
      return dimensionValueOptions;
    }
    return [];
  }, [metrics]);

  useEffect(() => {
    setDimensionValue(dimensionValueOptions[0]?.value);
  }, [dimensionValueOptions]);

  // Helper function to check if a value is a valid number (not NaN, not null, not undefined)
  const isValidNumber = useCallback(
    (value: number | null | undefined): value is number =>
      value !== null &&
      value !== undefined &&
      typeof value === 'number' &&
      !Number.isNaN(value),
    [],
  );

  // For one-sided anomaly directions only the relevant bound is populated by
  // the backend (DROP → min_value, SPIKE → max_value); the other side is null
  // by design. Requiring both bounds would filter out every point and render
  // the "Not enough data" empty state.
  const hasRequiredBounds = useCallback(
    (metric: {
      min_value?: number | null;
      max_value?: number | null;
    }): boolean => {
      if (anomalyDirection === AnomalyDirection.Drop) {
        return isValidNumber(metric.min_value);
      }
      if (anomalyDirection === AnomalyDirection.Spike) {
        return isValidNumber(metric.max_value);
      }
      return isValidNumber(metric.min_value) && isValidNumber(metric.max_value);
    },
    [anomalyDirection, isValidNumber],
  );

  const domainY = useMemo(() => {
    const valueArr: number[] = [];
    (metrics || [])
      .filter(
        (i) =>
          hasRequiredBounds(i) &&
          (!dimensionValue || i.dimension_value === dimensionValue),
      )
      .forEach((i) => {
        const value = i.value;
        const minValue = i.min_value;
        const maxValue = i.max_value;

        if (isValidNumber(value)) {
          valueArr.push(value);
        }
        if (isValidNumber(minValue)) {
          valueArr.push(minValue);
        }
        if (isValidNumber(maxValue)) {
          valueArr.push(maxValue);
        }
      });

    if (valueArr.length === 0) {
      return [0, 100];
    }

    valueArr.sort((a, b) => a - b);
    const min = Math.floor(valueArr[0]);
    const max = Math.ceil(valueArr[valueArr.length - 1]);
    const topMargin = (max - min) * 0.05;
    return [min, max + topMargin];
  }, [metrics, dimensionValue, hasRequiredBounds, isValidNumber]);

  const data: ChartMetric[] = useMemo(() => {
    const result = (metrics || [])
      .filter(hasRequiredBounds)
      .map((i) => {
        const minValue = isValidNumber(i.min_value) ? i.min_value : domainY[0];
        const maxValue = isValidNumber(i.max_value) ? i.max_value : domainY[1];
        return {
          ...i,
          fail_on_zero: failOnZero,
          anomaly_direction: anomalyDirection,
          EXPECTED_AREA: [minValue, maxValue],
          endTimeTimestamp: dayjs.utc(i.end_time).valueOf(),
        };
      })
      .sort((i, j) => i.endTimeTimestamp - j.endTimeTimestamp)
      .map((item, index) => ({
        ...item,
        index,
      }));

    const latestRunTime = result[result.length - 1]?.end_time;

    const filteredResult = dimensionValue
      ? result.filter((item) => item.dimension_value === dimensionValue)
      : result;

    return filteredResult.map((item, i) => {
      const inDetectionPeriod = isInDetectionPeriod(
        detectionPeriod,
        item.end_time,
        latestRunTime,
      );

      return {
        ...item,
        DELAY_AREA: i === result.length - 1 ? domainY : undefined,
        DETECTION_AREA: inDetectionPeriod ? domainY : undefined,
        dotsCount: result.length,
        isInDetectionPeriod: inDetectionPeriod,
      };
    });
  }, [
    metrics,
    failOnZero,
    anomalyDirection,
    domainY,
    detectionPeriod,
    dimensionValue,
    hasRequiredBounds,
    isValidNumber,
  ]);

  const dataWithDelayPeriod: ChartMetric[] = useMemo(() => {
    if (!detectionDelay?.count) {
      return data;
    }

    const lastItem = { ...data[data.length - 1] };

    const endTime = dayjs
      .utc(lastItem.end_time)
      .add(detectionDelay.count, detectionDelay.period)
      .toISOString();

    const delayItem = {
      end_time: endTime,
      DELAY_AREA: domainY,
      endTimeTimestamp: dayjs.utc(endTime).valueOf(),
      index: lastItem.index + 1,
    };

    return [...data, delayItem];
  }, [data, detectionDelay, domainY]);

  const latestRunTime = useMemo(() => data[data.length - 1]?.end_time, [data]);

  const trainingPeriodEnd = useMemo(() => {
    const trainingPeriodItems = data.filter(
      (item) =>
        !isInDetectionPeriod(detectionPeriod, item.end_time, latestRunTime),
    );

    return dayjs(
      trainingPeriodItems.sort(
        (a, b) => dayjs(b.end_time).valueOf() - dayjs(a.end_time).valueOf(),
      )[0]?.end_time,
    ).valueOf();
  }, [data, detectionPeriod, latestRunTime]);

  const chart = useChartSelection({
    data: dataWithDelayPeriod,
    getXValue: (i: ChartMetric) => i.endTimeTimestamp,
    chartRef,
    mode,
    onExcludeRange,
    trainingPeriodEnd,
  });

  const ticksX = useMemo(() => {
    if (chart.zoomedData.length <= DOTS_OVERLOAD_LIMIT) {
      // Use all ticks if there are not too much points
      return undefined;
    }
    const minTick = chart.zoomedData[0]?.endTimeTimestamp;
    const maxTick =
      chart.zoomedData[chart.zoomedData.length - 1]?.endTimeTimestamp;
    const ticksCount = 10;
    const step = Math.floor((maxTick - minTick) / ticksCount);
    const ticks: number[] = [];
    [...Array(ticksCount + 1).keys()].forEach((i) => {
      ticks.push(minTick + step * i);
    });
    return ticks;
  }, [chart.zoomedData]);

  const delayTimestamp = useMemo(() => {
    if (!detectionDelay || !latestRunTime) return null;

    return dayjs
      .utc(latestRunTime)
      .subtract(detectionDelay.count, detectionDelay.period)
      .valueOf();
  }, [detectionDelay, latestRunTime]);

  // Reset zoom when switching to exclude mode
  useEffect(() => {
    if (mode === 'exclude') {
      if (initialZoomRange) {
        chart.setZoomRange(initialZoomRange);
        chart.setZoom(1);
      } else {
        chart.handleResetZoom();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (!data.length || !detectionPeriod || !latestRunTime) return;

    const detectedData = data.filter((item) =>
      isInDetectionPeriod(detectionPeriod, item.end_time, latestRunTime),
    );

    if (detectedData.length < 2) return;

    const firstDetectionIndex = data.findIndex(
      (item) => item.end_time === detectedData[0].end_time,
    );
    const detectionStartTime = dayjs(data[firstDetectionIndex].end_time);
    const detectionEndTime = dayjs(
      detectedData[detectedData.length - 1].end_time,
    );

    // Calculate detection period in hours for better precision
    const detectionHours = Math.max(
      detectionEndTime.diff(detectionStartTime, 'hour', true),
      1,
    );

    const detectionRatio = 0.22;
    const trainingHours = Math.ceil(
      (detectionHours * (1 - detectionRatio)) / detectionRatio,
    );

    const trainingStartTime = detectionStartTime.subtract(
      trainingHours,
      'hour',
    );

    // Find index for training start time
    let trainingStartIndex = data.findIndex((item) =>
      dayjs(item.end_time).isAfter(trainingStartTime),
    );
    // If there are no actual samples in the desired training zoom, we need to shift the training start index to the previous sample
    if (trainingStartIndex === firstDetectionIndex) {
      trainingStartIndex = Math.max(firstDetectionIndex - 1, 0);
    }

    const startIndex = Math.max(0, trainingStartIndex);
    const endIndex = firstDetectionIndex + detectedData.length;

    const zoomStart = data[startIndex].endTimeTimestamp;
    const zoomEnd = data[Math.min(endIndex, data.length - 1)].endTimeTimestamp;

    const newZoomRange = { min: zoomStart, max: zoomEnd };

    chart.setZoomRange(newZoomRange);
    chart.setZoom(3);
    setInitialZoomRange(newZoomRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart.setZoomRange, chart.setZoom, data, detectionPeriod, latestRunTime]);

  const lineColor = useMemo(() => {
    const isConstantLineValue = chart.zoomedData.every(
      (item: ChartMetric) => item.value === chart.zoomedData[0].value,
    );

    const hasAnomaly = chart.zoomedData.some(
      (item: ChartMetric) => item.is_anomalous,
    );
    const isTrainingPeriod = chart.zoomedData.every(
      (item: ChartMetric) => !item.isInDetectionPeriod,
    );

    if (status === 'no_data' || isTrainingPeriod) {
      return themeBase.palette.common.gray18;
    }

    return isConstantLineValue
      ? hasAnomaly
        ? themeBase.palette.common.red
        : themeBase.palette.common.green
      : `url(#${id}colorUv)`;
  }, [status, chart.zoomedData, id]);

  const shouldShowAreaTitles = useMemo(() => status !== 'no_data', [status]);

  const hasNegativeMetricValue = useMemo(
    () =>
      (metrics || []).some(
        (m) =>
          (!dimensionValue || m.dimension_value === dimensionValue) &&
          isValidNumber(m.value) &&
          m.value < 0,
      ),
    [metrics, dimensionValue, isValidNumber],
  );

  const handleResetZoom = () => {
    if (initialZoomRange) {
      chart.setZoomRange(initialZoomRange);
      chart.setZoom(1);
    } else {
      chart.handleResetZoom();
    }
  };

  // Get timestamp preview from selection
  const selectionTimeText = chart.selectionTimeText
    ? `${formatShortDateTime(
        localizeDate(chart.selectionTimeText.timeStart),
      )} - ${formatShortDateTime(
        localizeDate(chart.selectionTimeText.timeEnd),
      )}`
    : null;

  // Calculate excluded samples for the dialog
  const excludedSamples = useMemo(() => {
    if (!chart.excludeRange) return [];

    const { start, end } = chart.excludeRange;

    return data
      .filter((item) => {
        const timestamp = item.endTimeTimestamp;
        return timestamp >= start && timestamp <= end;
      })
      .map((item) => ({
        timestamp: item.endTimeTimestamp,
      }));
  }, [chart.excludeRange, data]);

  return (
    <>
      <NoTextSelectWrapper
        ref={chartRef}
        $disableTextSelect={chart.selectionStartX !== null}
      >
        {metrics && metrics.length > 0 && (
          <ChartHeaderWrapper sx={{ gap: 4 }}>
            <HeaderLeftSection>
              {dimensionValueOptions.length > 0 && (
                <>
                  <OverflowTooltip
                    bold
                    sx={{
                      width: 'fit-content !important',
                    }}
                  >
                    {metrics[0].dimension}
                  </OverflowTooltip>
                  <Typography sx={{ fontWeight: 'bold', mr: 1 }}>: </Typography>

                  {dimensionValueOptions.length > 1 && (
                    <DimensionValueDropdown
                      value={dimensionValue}
                      options={dimensionValueOptions}
                      onChange={(e) => {
                        setDimensionValue(e);
                      }}
                      sortOptions={false}
                    />
                  )}

                  {dimensionValueOptions.length === 1 && (
                    <Typography>{dimensionValueOptions[0]?.label}</Typography>
                  )}
                </>
              )}

              {onExcludeRange && (
                <StyledToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(_, newMode) => newMode && setMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="zoom">ZOOM</ToggleButton>
                  <ToggleButton value="exclude">EXCLUDE</ToggleButton>
                </StyledToggleButtonGroup>
              )}
            </HeaderLeftSection>

            {chart.zoomedData.length > 0 && (
              <ChartZoomControls
                sx={
                  shouldShowAreaTitles ? {} : { margin: '12px 10px -16px auto' }
                }
                loading={loading}
                onZoomIn={chart.handleZoomIn}
                onZoomOut={chart.handleZoomOut}
                onReset={handleResetZoom}
                disableZoomIn={chart.isMaxZoom}
                disableZoomOut={!chart.zoomRange}
                disableReset={
                  initialZoomRange
                    ? chart.zoomRange === initialZoomRange
                    : !chart.zoomRange
                }
              />
            )}
          </ChartHeaderWrapper>
        )}

        {chart.zoomedData.length === 0 ? (
          <FlexCenter sx={{ height: '56px' }}>
            <Typography sx={{ color: 'common.gray2' }}>
              Not enough data to calculate anomaly score
            </Typography>
          </FlexCenter>
        ) : (
          <>
            {loading && (
              <LoadingWrapper>
                <CircularProgress size={32} />
              </LoadingWrapper>
            )}
            <ChartSelectionOverlay
              startX={chart.selectionStartX}
              currentX={
                mode === 'exclude'
                  ? chart.clampedSelectionCurrentX
                  : chart.selectionCurrentX
              }
              top={shouldShowAreaTitles ? 60 : 48}
            />
            {selectionTimeText &&
              chart.selectionStartX !== null &&
              chart.selectionCurrentX !== null && (
                <SelectionTimeIndicator>
                  {selectionTimeText}
                </SelectionTimeIndicator>
              )}

            <ResponsiveContainer width="99%" height={300}>
              <ComposedChart
                data={chart.zoomedData}
                margin={{ top: 28, right: 4, bottom: 14, left: 14 }}
                onMouseDown={chart.handleMouseDown}
                onMouseMove={chart.handleMouseMove}
                onMouseUp={() => chart.handleMouseUp()}
              >
                <defs>
                  <filter
                    id="lineShadow"
                    x="-20%"
                    y="-20%"
                    width="140%"
                    height="140%"
                  >
                    <feDropShadow
                      dx="-3"
                      dy="0"
                      stdDeviation="3"
                      floodColor="rgba(161,161,161,0.4)"
                    />
                  </filter>
                </defs>

                <defs>
                  <LinearGradient data={chart.zoomedData} id={id} />
                </defs>

                <CartesianGrid
                  stroke={themeBase.palette.common.gray16}
                  fill={themeBase.palette.common.white}
                />

                <XAxis
                  axisLine={false}
                  tickLine={false}
                  tickMargin={6}
                  tickFormatter={(value) =>
                    formatShortDateTime(localizeDate(value))
                  }
                  dataKey="endTimeTimestamp"
                  scale="time"
                  type="number"
                  domain={(range) => range}
                  tick={{ fontSize: 11 }}
                  ticks={ticksX}
                />

                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tickMargin={6}
                  dataKey="value"
                  domain={domainY}
                  tick={{ fontSize: 13 }}
                  tickFormatter={(value) => Humanize.compactInteger(value)}
                />

                <Tooltip
                  content={
                    <RenderTooltip
                      metricsTitle={metricsTitle}
                      hidden={chart.selectionStartX !== null || !dotHovered}
                      status={status}
                    />
                  }
                  isAnimationActive={false}
                  cursor={false}
                  allowEscapeViewBox={{ x: false, y: false }}
                  wrapperStyle={{ pointerEvents: 'none', outline: 0 }}
                />

                <Area
                  type="linear"
                  dataKey="EXPECTED_AREA"
                  stroke="#00"
                  fill="#EBEEF0"
                  opacity={0.4}
                  fillOpacity={1}
                  activeDot={false}
                  hide={loading}
                />

                <Area
                  type="linear"
                  dataKey="DELAY_AREA"
                  stroke="#00"
                  fill="#F8F8F8"
                  opacity={0.4}
                  fillOpacity={1}
                  activeDot={false}
                />

                <Area
                  type="linear"
                  dataKey="DETECTION_AREA"
                  stroke="#00"
                  fill="none"
                  activeDot={false}
                />

                {shouldShowAreaTitles && (
                  <>
                    {renderAreaTitles(
                      chart.zoomedData,
                      data,
                      latestRunTime,
                      detectionPeriod,
                      detectionDelay,
                    )}

                    {/* Detection line */}
                    {chart.firstDetectionTimestamp && (
                      <ReferenceLine
                        x={chart.firstDetectionTimestamp}
                        stroke={themeBase.palette.common.gray16}
                        strokeWidth={1}
                        isFront
                        style={{
                          filter:
                            'drop-shadow(-3px 0px 3px rgba(161, 161, 161, 0.4)',
                        }}
                      />
                    )}

                    {/* Delay line */}
                    {delayTimestamp && (
                      <ReferenceLine
                        x={delayTimestamp}
                        stroke={themeBase.palette.common.gray16}
                        strokeWidth={1}
                        isFront
                        style={{
                          filter:
                            'drop-shadow(-3px 0px 3px rgba(161, 161, 161, 0.4)',
                        }}
                      />
                    )}
                  </>
                )}

                <Line
                  dataKey="value"
                  stroke={lineColor}
                  strokeWidth={2}
                  dot={<Dot onDotHovered={setDotHovered} />}
                  activeDot={<Dot active onDotHovered={setDotHovered} />}
                  hide={loading}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </>
        )}

        <ExcludeConfirmationDialog
          open={chart.excludeDialogOpen}
          onClose={chart.handleExcludeCancel}
          onConfirm={chart.handleExcludeConfirm}
          startTime={chart.excludeRange?.start}
          endTime={chart.excludeRange?.end}
          excludedSamples={excludedSamples}
        />
      </NoTextSelectWrapper>
      {hasNegativeMetricValue && (
        <Alert severity="info" sx={{ margin: '16px 0 8px 0', width: '99%' }}>
          Negative values can appear when data collection is incomplete. If this
          issue persists, contact Elementary Support.
        </Alert>
      )}
    </>
  );
};

export default AnomalyChart;
